import { useState } from "react";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Textarea } from "@/components/ui/textarea";
import { Switch } from "@/components/ui/switch";
import { useTranslation } from "@/i18n/useTranslation";
import { useCreateVersion } from "../hooks";
import { useToast } from "@/hooks/use-toast";
import { logError } from "@/utils/errors";
import { startSpan } from "@/utils/sentry";
import { Loader2 } from "lucide-react";
import type { CreateVersionInput } from "../types";

interface CreateVersionFormProps {
  scriptId: string;
  onSuccess?: () => void;
  onCancel?: () => void;
}

export const CreateVersionForm = ({
  scriptId,
  onSuccess,
  onCancel,
}: CreateVersionFormProps) => {
  const { t } = useTranslation();
  const { toast } = useToast();
  const { mutateAsync: createVersion, isPending } = useCreateVersion();

  const [scriptUrl, setScriptUrl] = useState("");
  const [scriptUrlTh, setScriptUrlTh] = useState("");
  const [versionName, setVersionName] = useState("");
  const [notes, setNotes] = useState("");
  const [notesTh, setNotesTh] = useState("");
  const [passwordProtected, setPasswordProtected] = useState(false);
  const [reviewPassword, setReviewPassword] = useState("");

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();

    try {
      const input: CreateVersionInput = {
        scriptId,
        scriptUrl: scriptUrl.trim(),
        scriptUrlTh: scriptUrlTh.trim() || undefined,
        versionName: versionName.trim() || undefined,
        notes: notes.trim() || undefined,
        notesTh: notesTh.trim() || undefined,
        passwordProtected,
        reviewPassword: passwordProtected ? reviewPassword : undefined,
      };

      const newVersion = await startSpan(
        {
          op: "neon.insert",
          name: "script_versions.create",
          attributes: { scriptId },
        },
        () => createVersion(input)
      );

      toast({
        title: t("versions.version_created", { version: newVersion.version_number }),
      });

      onSuccess?.();
    } catch (error) {
      logError(error, {
        feature: "versions",
        action: "createVersion",
        scriptId,
      });
      toast({
        title: "Error creating version",
        description: error instanceof Error ? error.message : "Unknown error",
        variant: "destructive",
      });
    }
  };

  return (
    <form onSubmit={handleSubmit} className="space-y-4">
      <div className="space-y-2">
        <Label htmlFor="script-url">{t("versions.script_url")}</Label>
        <Input
          id="script-url"
          type="url"
          value={scriptUrl}
          onChange={(e) => setScriptUrl(e.target.value)}
          placeholder="https://..."
        />
      </div>

      <div className="space-y-2">
        <Label htmlFor="script-url-th">{t("versions.script_url_th")}</Label>
        <Input
          id="script-url-th"
          type="url"
          value={scriptUrlTh}
          onChange={(e) => setScriptUrlTh(e.target.value)}
          placeholder="https://..."
        />
      </div>

      <div className="space-y-2">
        <Label htmlFor="version-name">
          {t("versions.version_name")}
        </Label>
        <Input
          id="version-name"
          value={versionName}
          onChange={(e) => setVersionName(e.target.value)}
          placeholder={t("versions.version_name_placeholder")}
          maxLength={100}
        />
      </div>

      <div className="space-y-2">
        <Label htmlFor="notes">
          {t("versions.notes")}
        </Label>
        <Textarea
          id="notes"
          value={notes}
          onChange={(e) => setNotes(e.target.value)}
          placeholder={t("versions.notes_placeholder")}
          rows={3}
          maxLength={500}
        />
      </div>

      <div className="space-y-2">
        <Label htmlFor="notes-th">
          {t("versions.notes_th")}
        </Label>
        <Textarea
          id="notes-th"
          value={notesTh}
          onChange={(e) => setNotesTh(e.target.value)}
          placeholder={t("versions.notes_th_placeholder")}
          rows={3}
          maxLength={500}
        />
      </div>

      <div className="space-y-3 rounded-lg border p-4">
        <div className="flex items-center justify-between">
          <div className="space-y-0.5">
            <Label htmlFor="password-protected">
              {t("versions.password_protection")}
            </Label>
            <p className="text-sm text-muted-foreground">
              {t("versions.password_protection_hint")}
            </p>
          </div>
          <Switch
            id="password-protected"
            checked={passwordProtected}
            onCheckedChange={setPasswordProtected}
          />
        </div>

        {passwordProtected && (
          <div className="space-y-2 pt-2">
            <Label htmlFor="review-password">Password</Label>
            <Input
              id="review-password"
              type="text"
              value={reviewPassword}
              onChange={(e) => setReviewPassword(e.target.value)}
              placeholder="Enter password"
            />
          </div>
        )}
      </div>

      <div className="flex justify-end gap-2 pt-4">
        {onCancel && (
          <Button type="button" variant="outline" onClick={onCancel}>
            {t("common.cancel")}
          </Button>
        )}
        <Button type="submit" disabled={isPending}>
          {isPending && <Loader2 className="mr-2 h-4 w-4 animate-spin" />}
          {t("versions.create_new")}
        </Button>
      </div>
    </form>
  );
};
